import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import { CMButtonPopupSearchConditionCheckboxAndRadio } from "../../button-popup";
import { CMButtonSearchCondition } from "../CMButtonSearchCondition/CMButtonSearchCondition";
import {
  ComponentSingleStyleConfig,
  useDisclosure,
  useOutsideClick,
  useStyleConfig,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ValueObjectMetaEnum } from "../../../../lib";
import { hasValue } from "../../../../lib/util/common-util";

export type CMButtonSearchConditionCheckboxAndRadioProps<
  C extends [string, ...string[]],
  R extends [string, ...string[]],
> = {
  label: string;

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMetaCheckbox: ValueObjectMetaEnum<C>;

  /**
   * チェックボックスの値
   */
  checkboxValues?: C[number][] | null;

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMetaRadio: ValueObjectMetaEnum<R>;

  /**
   * ラジオボタンの値
   */
  radioValue?: R[number] | null;

  hideClearButton?: boolean;

  labelSubmit?: string;
  labelClear?: string;

  /**
   * ラジオボタンとチェックボックスで選択された値を呼び出し元に返却
   */
  onChange?: (
    selectedValue: {
      checkbox: C[number][] | null;
      radio: R[number] | null;
    } | null,
  ) => void;
} & ComponentStyleProps;

export const CMButtonSearchConditionCheckboxAndRadio = <
  C extends [string, ...string[]],
  R extends [string, ...string[]],
>({
  label,

  valueObjectMetaCheckbox,
  checkboxValues = null,

  valueObjectMetaRadio,
  radioValue = null,

  hideClearButton = false,

  labelSubmit,
  labelClear,

  onChange,

  sx,
  ...rest
}: CMButtonSearchConditionCheckboxAndRadioProps<C, R>) => {
  const styles = useStyleConfig(
    "CMButtonSearchConditionCheckboxAndRadioStyles",
  );

  const { t } = useTranslation();

  const displayValues = useMemo(() => {
    const displayValuesCheckbox =
      checkboxValues
        ?.map((v) => t(valueObjectMetaCheckbox.dict[v]))
        .filter(hasValue) ?? [];
    const displayValueRadio = hasValue(radioValue)
      ? t(valueObjectMetaRadio.dict[radioValue])
      : null;

    const _displayValues = [...displayValuesCheckbox, displayValueRadio].filter(
      hasValue,
    );

    if (_displayValues.length === 0) return undefined;
    return _displayValues;
  }, [
    t,
    valueObjectMetaCheckbox,
    valueObjectMetaRadio,
    checkboxValues,
    radioValue,
  ]);

  // 開閉制御
  const {
    isOpen: isOpenPopup,
    onClose: onClosePopup,
    onToggle: onTogglePopup,
  } = useDisclosure();

  const [checkboxInternalValues, setCheckboxInternalValues] = useState<
    C[number][]
  >(checkboxValues ?? []);
  useEffect(() => {
    setCheckboxInternalValues(checkboxInternalValues ?? []);
  }, [checkboxInternalValues]);

  const radioDefaultValue: R[number] = valueObjectMetaRadio.values[0];
  const [radioInternalValue, setRadioInternalValue] = useState<
    R[number] | null
  >(radioValue ?? radioDefaultValue);
  useEffect(() => {
    setRadioInternalValue(radioInternalValue ?? radioDefaultValue);
  }, [radioInternalValue, radioDefaultValue]);

  const resetAllInternalState = (
    newCheckboxValues: C[number][] | null = checkboxValues,
    newRadioValue: R[number] | null = radioValue,
  ) => {
    setCheckboxInternalValues(newCheckboxValues ?? []);
    setRadioInternalValue(newRadioValue);
  };

  const handleChangeInternalValue = ({
    checkbox,
    radio,
  }: {
    checkbox: C[number][];
    radio: R[number] | null;
  }) => {
    setCheckboxInternalValues(checkbox);
    setRadioInternalValue(radio);
  };

  /**
   * Submitボタン押下時、呼び出し元に選択値を返却する。
   */
  const handleSubmit = () => {
    onClosePopup();

    let newCheckboxValues: C[number][] | null;
    if (checkboxInternalValues.length <= 0) {
      newCheckboxValues = null;
      onChange?.(null);
    } else {
      // 次の処理で、正しい順序に並べ替える。
      newCheckboxValues = valueObjectMetaCheckbox.values.filter((v) =>
        checkboxInternalValues.includes(v),
      );
    }

    resetAllInternalState(newCheckboxValues, radioInternalValue);
    onChange?.({ checkbox: newCheckboxValues, radio: radioInternalValue });
  };

  /**
   * Clearボタン押下時、呼び出し元にnullを返却する。
   */
  const handleClear = () => {
    onClosePopup();
    resetAllInternalState(null, null);
    onChange?.(null);
  };

  const ref = useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      onClosePopup();
      resetAllInternalState();
    },
  });

  return (
    <CMButtonSearchCondition
      label={label}
      displayValue={displayValues}
      isOpenPopup={isOpenPopup}
      onClick={onTogglePopup}
      sx={{ ...styles, ...sx }}
      {...rest}
      ref={ref}
    >
      <CMButtonPopupSearchConditionCheckboxAndRadio<C, R>
        valueObjectMetaCheckbox={valueObjectMetaCheckbox}
        checkboxInternalValues={checkboxInternalValues}
        valueObjectMetaRadio={valueObjectMetaRadio}
        radioInternalValue={radioInternalValue}
        labelSubmit={labelSubmit}
        labelClear={labelClear}
        hideClearButton={hideClearButton}
        onChangeInternalValues={handleChangeInternalValue}
        onSubmit={handleSubmit}
        onClear={handleClear}
      />
    </CMButtonSearchCondition>
  );
};

export const CMButtonSearchConditionCheckboxAndRadioStyles: ComponentSingleStyleConfig =
  {
    baseStyle: {},
  };
